import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePermissions } from '@/contexts/PermissionContext';
import { useFeatureConfig } from '@/contexts/FeatureConfigContext';
import { hasMessagingSurfaceAccess } from '@/utils/messagingAccess';

const API = process.env.REACT_APP_BACKEND_URL;

/** Module required for each profile hub card (matches feature_registry plan modules). */
const CARD_MODULE_MAP = {
  documents: 'Documents',
  compliance: 'HRM',
  company_policies: 'Policy',
  leave_availability: 'HRM',
  my_shifts: 'Scheduling',
  my_incidents: 'Incidents',
  auditor_portal: 'Auditor',
  interview_availability: 'Recruitment',
  open_shifts: 'Scheduling',
  my_applications: 'Recruitment',
  internal_jobs: 'Recruitment',
  my_learning: 'Learning',
};

/** Extra permission beyond card.permission from registry. */
const CARD_EXTRA_PERMISSIONS = {
  leave_availability: 'leave.request.view_own',
  open_shifts: 'scheduling.shift.view',
  interview_availability: null, // availability OR conduct checked below
  // messages: gated via canAccessMessages (group OR shift/on-call surface)
};

/**
 * Unified profile hub access: module + permission + card visibility.
 */
export function useProfileHubAccess() {
  const { hasPermission } = usePermissions();
  const { shouldShowCard, getCard, hasModule, ensureSecondaryConfig, secondaryLoading } = useFeatureConfig();
  const [messagingCapabilities, setMessagingCapabilities] = useState(null);

  useEffect(() => {
    ensureSecondaryConfig();
  }, [ensureSecondaryConfig]);

  useEffect(() => {
    if (!hasModule('Messaging')) {
      setMessagingCapabilities(null);
      return;
    }
    let cancelled = false;
    const token = localStorage.getItem('access_token');
    if (!token) return undefined;

    (async () => {
      try {
        const response = await fetch(`${API}/api/scheduling/messaging/capabilities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (!cancelled) setMessagingCapabilities(data);
      } catch {
        // Keep sync fallback in hasMessagingSurfaceAccess
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasModule]);

  const canAccessCard = useCallback(
    (cardKey) => {
      if (!shouldShowCard(cardKey)) return false;

      const requiredModule = CARD_MODULE_MAP[cardKey];
      if (requiredModule && !hasModule(requiredModule)) return false;

      const card = getCard(cardKey);
      if (card?.permission && !hasPermission(card.permission)) return false;

      const extra = CARD_EXTRA_PERMISSIONS[cardKey];
      if (extra && !hasPermission(extra)) return false;

      if (cardKey === 'messages') {
        return hasMessagingSurfaceAccess({
          hasModule,
          hasPermission,
          capabilities: messagingCapabilities,
        });
      }

      if (cardKey === 'interview_availability') {
        return (
          hasPermission('recruitment.interviews.availability') ||
          hasPermission('recruitment.interviews.conduct')
        );
      }

      return true;
    },
    [shouldShowCard, getCard, hasPermission, hasModule, messagingCapabilities]
  );

  const canAccessMessages = useMemo(
    () =>
      hasMessagingSurfaceAccess({
        hasModule,
        hasPermission,
        capabilities: messagingCapabilities,
      }),
    [hasModule, hasPermission, messagingCapabilities]
  );

  const canAccessScheduling = useMemo(
    () => hasModule('Scheduling') && canAccessCard('my_shifts'),
    [hasModule, canAccessCard]
  );

  return {
    canAccessCard,
    canAccessMessages,
    canAccessScheduling,
    hasModule,
    hasPermission,
    messagingCapabilities,
    cardsLoading: secondaryLoading,
  };
}

export default useProfileHubAccess;
